import Head from "next/head";
import React, { FC } from "react";
import { useRouter } from "next/router";
import { PageSeoData } from "@/types/seo";
import {
  getSeoData,
  resolvePageSeo,
  SEO_CONSTANTS,
  buildPageUrls,
} from "@/config/seo";
import { languageCodeToLanCode } from "@/utils/LocaleUtil";
import { useLocale } from "@/store/locale-store";

type Props = {
  seo?: Partial<PageSeoData>;
};

const Header: FC<Props> = ({ seo }) => {
  const { languageCode } = useLocale();
  const router = useRouter();

  // 넘긴 칸만 덮고 빈 칸은 언어별 정적 SEO 로 — 이유는 `resolvePageSeo` 주석.
  const data = resolvePageSeo(
    seo,
    getSeoData(router.pathname, languageCodeToLanCode(languageCode)),
  );

  // canonical · hreflang — 사이트맵과 같은 모양(로케일 세그먼트 · 끝 슬래시 · 쿼리 없음). 이유는 `buildPageUrls` 주석.
  const urls = buildPageUrls({
    asPath: router.asPath,
    languageCode,
    canonicalPath: seo?.canonicalPath,
  });
  const canonicalUrl = urls.canonical;

  // OG 이미지 (동적 또는 기본값)
  const ogImage = data.ogImage ?? SEO_CONSTANTS.DEFAULT_OG_IMAGE;
  const ogImageUrl = ogImage.startsWith("http")
    ? ogImage
    : `${SEO_CONSTANTS.BASE_URL}${ogImage}`;

  // OG 타입
  const ogType = data.ogType ?? "website";

  return (
    <Head>
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0, height=device-height"
      />
      <title>{data.title}</title>
      <meta name="description" content={data.description} />
      <link rel="icon" href="/favicon.ico" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang 태그 */}
      {urls.alternates.map(({ hrefLang, href }) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={urls.xDefault} />

      {/* noindex 지원 */}
      {data.noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* keywords */}
      {"keywords" in data && data.keywords && data.keywords.length > 0 && (
        <meta name="keywords" content={data.keywords.join(", ")} />
      )}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={data.title} />
      <meta property="og:site_name" content={SEO_CONSTANTS.SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:description" content={data.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />

      {/* Article 메타 (뉴스/블로그용) */}
      {"publishedTime" in data && data.publishedTime && (
        <meta property="article:published_time" content={data.publishedTime} />
      )}
      {"modifiedTime" in data && data.modifiedTime && (
        <meta property="article:modified_time" content={data.modifiedTime} />
      )}

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="twitter:domain" content="bhaptics.com" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={data.title} />
      <meta name="twitter:description" content={data.description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* JSON-LD 구조화된 데이터 */}
      {"jsonLd" in data &&
        data.jsonLd?.map((ld, i) => (
          <script
            key={i}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
          />
        ))}
    </Head>
  );
};

export default Header;
